import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/customSupabaseClient';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Trash2 } from 'lucide-react';

/**
 * FEATURE: Remove Program from User
 * LOCATION: src/components/RemoveProgramForm.jsx
 * DESCRIPTION: This component handles the logic for manually removing (soft-deleting) a program from a specific user email via Supabase RPC.
 */
const RemoveProgramForm = ({ onProgramRemoved }) => {
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(true);

  useEffect(() => {
    const fetchPrograms = async () => {
      setLoadingPrograms(true);
      try {
        const { data, error } = await supabase.from('programs').select('id, name').eq('is_active', true);
        if (error) throw error;
        setPrograms(data || []);
      } catch (error) {
        toast.error("Error fetching programs", { description: "Could not load the list of programs." });
        console.error('Error fetching programs:', error);
      } finally {
        setLoadingPrograms(false);
      }
    };

    fetchPrograms();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !selectedProgram) {
      toast.error("Missing information", { description: "Please provide both an email and a program." });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('my_remove_program', {
        p_athlete_email: email,
        p_program_id: selectedProgram,
      });

      if (error) throw error;

      if (data && data.success === false) {
         throw new Error(data.error || 'Failed to remove program');
      }

      toast.success(`Program successfully removed from ${email}.`);
      setEmail('');
      setSelectedProgram('');
      if (onProgramRemoved) {
        onProgramRemoved();
      }
    } catch (error) {
      toast.error("Removal Failed", { description: error.message || "There was a problem removing the program." });
      console.error('Error removing program:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full border-destructive/20" data-testid="remove-program-form">
      <CardHeader>
        <CardTitle className="text-destructive flex items-center gap-2">
          <Trash2 className="h-5 w-5" />
          Remove Program from User
        </CardTitle>
        <CardDescription>
          Manually revoke a user's access to a program by their email address.
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="remove-email">User Email</Label>
            <Input
              id="remove-email"
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="remove-program">Program to Remove</Label>
            <Select onValueChange={setSelectedProgram} value={selectedProgram} disabled={loadingPrograms}>
              <SelectTrigger id="remove-program">
                <SelectValue placeholder={loadingPrograms ? "Loading programs..." : "Select a program"} />
              </SelectTrigger>
              <SelectContent>
                {programs.map((program) => (
                  <SelectItem key={program.id} value={program.id}>
                    {program.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
        <CardFooter>
          <Button type="submit" variant="destructive" disabled={loading || loadingPrograms} className="w-full sm:w-auto">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Remove Program Access
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default RemoveProgramForm;